import React from 'react';
import { defineMessages } from 'react-intl';
import { DeleteOutlined, FileZipOutlined, PlusOutlined } from '@ant-design/icons';
// import notDataImage from '@assets/icons/no-data.svg';
import ListPage from '@components/common/layout/ListPage';
import PageWrapper from '@components/common/layout/PageWrapper';
import { AppConstants, DEFAULT_TABLE_ITEM_UNLIMIT, PRINTER_MAPPING_MEDIA_KIND } from '@constants';
import apiConfig from '@constants/apiConfig';
import { mediaCategoryOptions } from '@constants/masterData';
import useFetch from '@hooks/useFetch';
import useListBase from '@hooks/useListBase';
import useTranslate from '@hooks/useTranslate';
import { commonMessage } from '@locales/intl';
import { showErrorMessage, showSucsessMessage } from '@services/notifyService';
import { getFullImageUrl } from '@utils';
import { Button, Flex, Modal, Tabs, Tooltip, Upload } from 'antd';

import styles from './index.module.scss';

const message = defineMessages({
    addIcon: { id: 'contentLibrary.addIcon', defaultMessage: 'Add icon' },
    importZip: { id: 'contentLibrary.importZip', defaultMessage: 'Import ZIP' },
    deleteIcon: { id: 'contentLibrary.deleteIcon', defaultMessage: 'Delete icon' },
    deleteTitle: { id: 'contentLibrary.deleteTitle', defaultMessage: 'Delete this icon?' },
    iconAdded: { id: 'contentLibrary.iconAdded', defaultMessage: 'Icon added' },
    iconAddedDescription: {
        id: 'contentLibrary.iconAddedDescription',
        defaultMessage: '{name} is now available in this library.',
    },
    iconDeleted: { id: 'contentLibrary.iconDeleted', defaultMessage: 'Icon deleted' },
    fileTypes: { id: 'contentLibrary.fileTypes', defaultMessage: 'Images: max 1 MB each · ZIP: max 20 MB' },
    unsupportedFile: { id: 'contentLibrary.unsupportedFile', defaultMessage: 'Unsupported file' },
    unsupportedFileDescription: {
        id: 'contentLibrary.unsupportedFileDescription',
        defaultMessage: 'Choose an SVG, PNG, JPG or WebP icon.',
    },
    fileTooLarge: { id: 'contentLibrary.fileTooLarge', defaultMessage: 'Icon is too large' },
    fileTooLargeDescription: {
        id: 'contentLibrary.fileTooLargeDescription',
        defaultMessage: 'The maximum file size is 1 MB.',
    },
    invalidZip: { id: 'contentLibrary.invalidZip', defaultMessage: 'Unable to import ZIP' },
    invalidZipDescription: {
        id: 'contentLibrary.invalidZipDescription',
        defaultMessage: 'Choose a valid ZIP containing SVG, PNG, JPG or WebP images.',
    },
    zipTooLarge: { id: 'contentLibrary.zipTooLarge', defaultMessage: 'ZIP is too large' },
    zipTooLargeDescription: {
        id: 'contentLibrary.zipTooLargeDescription',
        defaultMessage: 'ZIP files may be up to 20 MB and contain up to 200 files.',
    },
    zipImported: { id: 'contentLibrary.zipImported', defaultMessage: 'ZIP imported' },
    zipImportedDescription: {
        id: 'contentLibrary.zipImportedDescription',
        defaultMessage: '{count} images added. {skipped} files skipped.',
    },
    noIcons: { id: 'contentLibrary.noIcons', defaultMessage: 'No icons yet' },
    noIconsDescription: {
        id: 'contentLibrary.noIconsDescription',
        defaultMessage: 'Add an SVG, PNG, JPG or WebP icon to begin.',
    },
});

const MediaListPage = () => {
    const translate = useTranslate();
    const queryParam = new URLSearchParams(location.search);
    const kind = queryParam.get('kind') || PRINTER_MAPPING_MEDIA_KIND;
    const { execute: executeCreate, loading: loadingCreate } = useFetch(apiConfig.file.uploadTemplate, {
        immediate: false,
    });

    const { execute: executeDelete, loading: loadingDelete } = useFetch(apiConfig.file.deleteFile, {
        immediate: false,
    });

    const { data, mixinFuncs, queryFilter, loading, pagination, queryParams, serializeParams, setLoading } = useListBase({
        apiConfig: apiConfig.templateImage,
        options: {
            pageSize: DEFAULT_TABLE_ITEM_UNLIMIT,
            objectName: translate.formatMessage(commonMessage.media),
        },
        override: (funcs) => {
            funcs.mappingData = (response) => {
                if (response.result === true) {
                    return {
                        data: response.data.content,
                        total: response.data.totalElements,
                    };
                }
            };
            funcs.getList = () => {
                const params = mixinFuncs.prepareGetListParams(queryFilter);
                mixinFuncs.handleFetchList({ kind: kind, ...params });
            };
        },
    });

    const onUploadFile = ({ file, onSuccess, onError }) => {
        const isZip = file.type === 'application/zip' || file.name.endsWith('.zip');

        if (!isZip) {
            showErrorMessage(translate.formatMessage(message.invalidZip), translate);
            return;
        }

        if (file.size > 20 * 1024 * 1024) {
            showErrorMessage(translate.formatMessage(message.zipTooLargeDescription), translate);
            return;
        }

        setLoading(true);
        executeCreate({
            data: {
                file: file,
                kind,
            },
            onCompleted: (response) => {
                if (response.result === true) {
                    mixinFuncs.getList();
                    showSucsessMessage(translate.formatMessage(commonMessage.uploadMediaSuccess), translate);
                }
            },
            onError: (error) => {
                setLoading(false);
                showErrorMessage(translate.formatMessage(commonMessage.uploadMediaFail), translate);
            },
        });
    };

    const onUploadIcon = ({ file, onSuccess, onError }) => {
        if (file.size > 1 * 1024 * 1024) {
            showErrorMessage(translate.formatMessage(message.fileTooLargeDescription), translate);
            return;
        }

        setLoading(true);
        executeCreate({
            data: {
                file: file,
                kind,
            },
            onCompleted: (response) => {
                if (response.result === true) {
                    mixinFuncs.getList();
                    showSucsessMessage(translate.formatMessage(commonMessage.uploadMediaSuccess), translate);
                }
            },
            onError: (error) => {
                setLoading(false);
                showErrorMessage(translate.formatMessage(commonMessage.uploadMediaFail), translate);
            },
        });
    };

    const removeIcon = (icon) => {
        Modal.confirm({
            title: translate.formatMessage(message.deleteTitle),
            content: '',
            okText: translate.formatMessage(commonMessage.ok),
            cancelText: translate.formatMessage(commonMessage.cancel),
            centered: true,
            onOk: () => {
                executeDelete({
                    data: {
                        filePath: icon,
                    },
                    onCompleted: (response) => {
                        if (response.result === true) {
                            showSucsessMessage(translate.formatMessage(message.iconDeleted), translate);
                            mixinFuncs.getList();
                        }
                    },
                    onError: (error) => {
                        showErrorMessage(translate.formatMessage(commonMessage.error), translate);
                    },
                });
            },
        });
    };

    return (
        <PageWrapper
            routes={[
                { breadcrumbName: translate.formatMessage(commonMessage.media) },
            ]}
        >
            <ListPage
                searchForm={
                    <div style={{ marginBottom: 16 }}>
                        <Tabs
                            activeKey={String(kind)}
                            onChange={(key) => mixinFuncs.setQueryParams({ kind: key })}
                            items={mediaCategoryOptions.map((item) => ({
                                key: String(item.value),
                                label: translate.formatMessage(item.label),
                            }))}
                            tabBarExtraContent={
                                <Flex gap={8} align="center">
                                    <span style={{ color: '#999', fontSize: 14, whiteSpace: 'nowrap' }}>
                                        {translate.formatMessage(message.fileTypes)}
                                    </span>
                                    <Upload
                                        accept=".svg,.png,.jpg,.jpeg,.webp"
                                        name="field-upload-icon"
                                        customRequest={onUploadIcon}
                                        showUploadList={false}
                                        disabled={loadingCreate}
                                    >
                                        <Button icon={<PlusOutlined />} loading={loadingCreate} type='primary'>
                                            {translate.formatMessage(message.addIcon)}
                                        </Button>
                                    </Upload>
                                    <Upload
                                        accept=".zip"
                                        name="field-upload"
                                        customRequest={onUploadFile}
                                        showUploadList={false}
                                        className={styles.customZip}
                                        disabled={loadingCreate}
                                    >
                                        <Button icon={<FileZipOutlined />} loading={loadingCreate}>
                                            {translate.formatMessage(message.importZip)}
                                        </Button>
                                    </Upload>
                                </Flex>
                            }
                        />
                    </div>
                }
                baseTable={
                    <div className={styles.gridWrapper}>
                        {data?.length > 0 ? (
                            data.map((item) => {
                                const fileName = item ? item.split('/').pop() : '';

                                return (
                                    <div key={item} className={styles.imageWrapper}>
                                        <img
                                            src={getFullImageUrl(item, AppConstants.templateRootUrl)}
                                            alt=""
                                        />
                                        <div className={styles.iconFooter}>
                                            <Tooltip title={fileName}>
                                                <span>{fileName}</span>
                                            </Tooltip>

                                            <Tooltip title={translate.formatMessage(message.deleteIcon)}>
                                                <Button
                                                    className={styles.deleteButton}
                                                    type="text"
                                                    danger
                                                    icon={<DeleteOutlined />}
                                                    onClick={() => removeIcon(item)}
                                                />
                                            </Tooltip>
                                        </div>
                                    </div>
                                );
                            })
                        ) : (
                            <Flex style={{ gridColumn: "1 / -1", textAlign: "center", marginTop: 20 }} vertical>
                                {/* <img src={notDataImage} alt="" /> */}
                                <span style={{ opacity: 0.5 }}>No data</span>
                            </Flex>
                        )}
                    </div>
                }
                loading={loading || loadingDelete}
            ></ListPage>
        </PageWrapper>
    );
};

export default MediaListPage;
